import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { usePolling } from '../lib/usePolling';
import ProductCard from '../components/ProductCard';
import InfluencerStrip from '../components/InfluencerStrip';
import VideoFeed from '../components/VideoFeed';
import AuthModal from '../components/AuthModal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, X, SlidersHorizontal, MapPin, Droplets, BadgeCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ['Cleanser', 'Toner', 'Serum', 'Moisturizer', 'Sunscreen', 'Mask', 'Makeup', 'Hair', 'Body', 'Other'];
const SKIN_TYPES = ['Dry', 'Normal', 'Oily', 'Combination'];
const SKIN_CONCERNS = ['Acne', 'Sensitive', 'Fine lines', 'Big pores', 'Acne scars', 'Hyperpigmentation', 'Freckles', 'Lack of elasticity'];

export default function HomePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAuth, setShowAuth] = useState(false);

  // Filters
  const [category, setCategory] = useState('');
  const [skinType, setSkinType] = useState('');
  const [skinConcern, setSkinConcern] = useState('');
  const hasFilters = category || skinType || skinConcern;

  // Search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const searchTimeout = useRef(null);
  const searchRef = useRef(null);

  // Close search dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const fetchListings = useCallback(async (retries = 2) => {
    for (let i = 0; i <= retries; i++) {
      try {
        const params = {};
        if (category) params.category = category;
        if (skinType) params.skin_type = skinType;
        if (skinConcern) params.skin_concern = skinConcern;
        const res = await axios.get(`${API}/listings`, { params });
        setListings(res.data);
        setLoading(false);
        return;
      } catch (err) {
        console.error(`Failed to fetch listings (attempt ${i + 1})`, err);
        if (i < retries) await new Promise(r => setTimeout(r, 1500 * (i + 1)));
      }
    }
    setLoading(false);
  }, [category, skinType, skinConcern]);

  usePolling(fetchListings, 20000);

  // Restore scroll position when returning from a listing detail page
  useEffect(() => {
    if (!loading) {
      const savedScroll = sessionStorage.getItem('homepage_scroll');
      if (savedScroll) {
        setTimeout(() => window.scrollTo(0, parseInt(savedScroll, 10)), 50);
        sessionStorage.removeItem('homepage_scroll');
      }
    }
  }, [loading]);

  // Auto-scroll to listings when navigated with ?scroll=listings
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('scroll') === 'listings' && !loading) {
      setTimeout(() => {
        const el = document.getElementById('listings-grid-section');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [location.search, loading]);

  // Scroll to hash anchor (e.g. #from-creators) — only on initial page load from another page
  useEffect(() => {
    if (location.hash && !loading) {
      const timer = setTimeout(() => {
        const el = document.getElementById(location.hash.slice(1));
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
          // Clean hash to prevent re-triggering
          window.history.replaceState(null, '', window.location.pathname);
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [location.hash, loading]);

  // Debounced search
  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (!searchQuery || searchQuery.trim().length < 2) {
      setSearchResults(null);
      setSearching(false);
      return;
    }
    setSearching(true);
    searchTimeout.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/search`, { params: { q: searchQuery } });
        setSearchResults(res.data);
      } catch {
        setSearchResults({ listings: [], sellers: [] });
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => { if (searchTimeout.current) clearTimeout(searchTimeout.current); };
  }, [searchQuery]);

  const clearFilters = () => {
    setCategory('');
    setSkinType('');
    setSkinConcern('');
  };

  const formatPrice = (price) => `Rp ${Number(price).toLocaleString('id-ID')}`;

  return (
    <div className="min-h-screen bg-[#FCFAF8]">
      <Header />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Search Bar */}
        <section className="mb-3 flex justify-center" data-testid="search-section">
          <div className="w-full max-w-xl relative" ref={searchRef}>
            <div className="relative">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A89F91]" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                placeholder="Search products, brands, or sellers..."
                className="w-full bg-white border border-cream-100 rounded-2xl pl-11 pr-10 py-3.5 font-body text-sm text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-blush-200 focus:border-transparent shadow-[0_4px_20px_rgb(0,0,0,0.04)] transition-all placeholder:text-[#A89F91]"
                data-testid="search-input"
              />
              {searchQuery && (
                <button onClick={() => { setSearchQuery(''); setSearchResults(null); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A89F91] hover:text-[#2C2A29] transition-colors"
                  data-testid="search-clear">
                  <X size={16} />
                </button>
              )}
            </div>

            {/* Search Results Dropdown */}
            {searchFocused && searchQuery.length >= 2 && (
              <div className="absolute top-full mt-2 w-full bg-white rounded-2xl border border-cream-100 shadow-[0_20px_60px_rgb(0,0,0,0.08)] z-50 max-h-[400px] overflow-y-auto" data-testid="search-results-dropdown">
                {searching ? (
                  <div className="p-6 text-center">
                    <div className="w-6 h-6 border-2 border-blush-200 border-t-blush-400 rounded-full animate-spin mx-auto" />
                  </div>
                ) : searchResults ? (
                  <>
                    {/* Sellers */}
                    {searchResults.sellers.length > 0 && (
                      <div className="p-3">
                        <p className="text-[10px] font-body uppercase tracking-[0.2em] font-semibold text-[#A89F91] px-2 mb-2">Sellers</p>
                        {searchResults.sellers.map(seller => (
                          <button key={seller.user_id}
                            onClick={() => { navigate(`/profile/${seller.user_id}`); setSearchFocused(false); setSearchQuery(''); }}
                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-cream-50 transition-colors text-left"
                            data-testid={`search-seller-${seller.user_id}`}>
                            {seller.picture ? (
                              <img src={seller.picture} alt="" className="w-9 h-9 rounded-full object-cover border-2 border-blush-200" />
                            ) : (
                              <div className="w-9 h-9 rounded-full bg-blush-200 flex items-center justify-center text-sm font-bold text-[#2C2A29]">
                                {seller.name?.[0]}
                              </div>
                            )}
                            <div className="min-w-0">
                              <p className="font-body text-sm font-medium text-[#2C2A29] truncate flex items-center gap-1.5">
                                {seller.name}
                                {seller.is_influencer && <BadgeCheck size={14} className="text-blue-500 fill-blue-50 flex-shrink-0" />}
                              </p>
                              <div className="flex items-center gap-2 text-xs font-body text-[#A89F91]">
                                {seller.skin_type && <span><Droplets size={10} className="inline mr-0.5" />{seller.skin_type}</span>}
                                {seller.city && <span><MapPin size={10} className="inline mr-0.5" />{seller.city}</span>}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Divider */}
                    {searchResults.sellers.length > 0 && searchResults.listings.length > 0 && (
                      <div className="border-t border-cream-100/50 mx-3" />
                    )}

                    {/* Listings */}
                    {searchResults.listings.length > 0 && (
                      <div className="p-3">
                        <p className="text-[10px] font-body uppercase tracking-[0.2em] font-semibold text-[#A89F91] px-2 mb-2">Products</p>
                        {searchResults.listings.map(listing => (
                          <button key={listing.listing_id}
                            onClick={() => { navigate(`/listing/${listing.listing_id}`); setSearchFocused(false); setSearchQuery(''); }}
                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-cream-50 transition-colors text-left"
                            data-testid={`search-listing-${listing.listing_id}`}>
                            <img src={listing.photos?.[0] || 'https://via.placeholder.com/48'}
                              alt="" className={`w-10 h-10 rounded-lg object-cover flex-shrink-0 ${listing.sold_out ? 'opacity-50 grayscale' : ''}`} />
                            <div className="min-w-0 flex-1">
                              <p className="font-body text-sm font-medium text-[#2C2A29] truncate">{listing.product_name}</p>
                              <p className="text-xs font-body text-[#A89F91]">{listing.brand} · {formatPrice(listing.price)}</p>
                            </div>
                            {listing.sold_out && (
                              <span className="text-[10px] font-body font-semibold text-[#78716C] bg-cream-100 px-2 py-0.5 rounded-full flex-shrink-0">Sold out</span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* No results */}
                    {searchResults.listings.length === 0 && searchResults.sellers.length === 0 && (
                      <div className="p-8 text-center">
                        <Search size={28} className="mx-auto text-cream-200 mb-2" />
                        <p className="font-body text-sm text-[#78716C]">No results for "{searchQuery}"</p>
                      </div>
                    )}
                  </>
                ) : null}
              </div>
            )}
          </div>
        </section>

        {/* Hero Section */}
        <section className="mb-4 relative overflow-hidden rounded-2xl" data-testid="hero-section">
          <div className="absolute inset-0">
            <img src="https://images.unsplash.com/photo-1583209814683-c023dd293cc6?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=80&w=800" alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#FFF5F5]/95 via-[#FDE8E8]/90 to-[#FDE8E8]/50" />
          </div>
          <div className="relative z-10 px-5 py-5 sm:px-8 sm:py-6">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="bg-[#D48585] text-white text-[9px] font-body font-bold px-2 py-0.5 rounded-full uppercase tracking-wider" data-testid="beta-badge">Beta</span>
            </div>
            <h1 className="font-heading text-xl sm:text-2xl lg:text-3xl tracking-tight text-[#2C2A29] mb-1.5 leading-tight drop-shadow-sm">
              Give your beauty stash a <span className="text-[#D48585]">second life</span>
            </h1>
            <p className="font-body text-xs sm:text-sm text-[#3D3A38] font-semibold max-w-sm leading-relaxed drop-shadow-sm">
              Buy and sell beauty products from real people.
            </p>
          </div>
        </section>

        {/* Influencer Strip */}
        <InfluencerStrip />

        {/* Filter Bar */}
        <section className="mb-4" data-testid="filter-section">
          <div className="flex items-center gap-2 overflow-x-auto hide-scrollbar pb-1">
            <div className="flex items-center gap-1.5 text-sm font-body font-medium text-[#78716C] flex-shrink-0">
              <SlidersHorizontal size={16} />
              <span>Filters</span>
            </div>

            <Select value={category} onValueChange={(v) => setCategory(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[140px] flex-shrink-0 rounded-xl h-10 bg-white border-cream-100 font-body text-sm" data-testid="filter-category">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-[#A89F91]">All Categories</SelectItem>
                {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select value={skinType} onValueChange={(v) => setSkinType(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[160px] rounded-xl h-10 bg-white border-cream-100 font-body text-sm" data-testid="filter-skin-type">
                <SelectValue placeholder="Skin Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-[#A89F91]">All Skin Types</SelectItem>
                {SKIN_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select value={skinConcern} onValueChange={(v) => setSkinConcern(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[150px] flex-shrink-0 rounded-xl h-10 bg-white border-cream-100 font-body text-sm" data-testid="filter-skin-concern">
                <SelectValue placeholder="Skin Concern" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-[#A89F91]">All Concerns</SelectItem>
                {SKIN_CONCERNS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>

            {hasFilters && (
              <button onClick={clearFilters}
                className="rounded-full px-4 py-2 text-xs font-body font-medium text-[#E05A5A] bg-red-50 hover:bg-red-100 transition-colors flex items-center gap-1"
                data-testid="clear-filters-btn">
                <X size={12} /> Clear
              </button>
            )}
          </div>
        </section>

        {/* Listings Header */}
        <div className="flex items-center justify-between mb-4" id="listings-grid-section">
          <h2 className="font-heading text-xl sm:text-2xl text-[#2C2A29]">
            {hasFilters ? 'Filtered Results' : 'New in Stash'}
          </h2>
          <span className="text-sm font-body text-[#A89F91]">{listings.length} products</span>
        </div>

        {/* Listings Grid */}
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-white rounded-2xl sm:rounded-3xl p-2.5 sm:p-4 animate-pulse">
                <div className="aspect-square rounded-xl sm:rounded-2xl bg-cream-100 mb-2 sm:mb-3" />
                <div className="h-2.5 sm:h-3 bg-cream-100 rounded w-1/3 mb-1.5 sm:mb-2" />
                <div className="h-3 sm:h-4 bg-cream-100 rounded w-2/3 mb-1.5 sm:mb-2" />
                <div className="h-4 sm:h-5 bg-cream-100 rounded w-1/3" />
              </div>
            ))}
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-20" data-testid="empty-listings">
            <Search size={48} className="mx-auto text-cream-200 mb-4" />
            <h3 className="font-heading text-xl text-[#2C2A29] mb-2">
              {hasFilters ? 'No products match your filters' : 'No listings yet'}
            </h3>
            <p className="font-body text-[#78716C]">
              {hasFilters ? 'Try adjusting your filters' : 'Be the first to list a beauty product!'}
            </p>
            {hasFilters && (
              <button onClick={clearFilters}
                className="mt-4 text-sm font-body font-medium text-[#C5A880] hover:underline"
                data-testid="empty-clear-filters">Clear all filters</button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6" data-testid="listings-grid">
            {listings.map((listing) => (
              <ProductCard key={listing.listing_id} listing={listing} onAuthRequired={() => setShowAuth(true)} />
            ))}
          </div>
        )}

        {/* Influencer Video Content */}
        <div className="mt-12">
          <VideoFeed />
        </div>
        {/* Spacer to allow scroll-to-section to position title near top of viewport */}
        <div className="h-24 sm:h-16" aria-hidden="true" />
      </main>

      <Footer />
      <AuthModal open={showAuth} onOpenChange={setShowAuth} onSuccess={() => {
        const saved = sessionStorage.getItem('pending_listing_action');
        if (saved) {
          const { listingId, action } = JSON.parse(saved);
          // Navigate to the listing detail page — the pending action will fire there
          navigate(`/listing/${listingId}`);
        }
      }} />
    </div>
  );
}
